import { AxiosError } from 'axios'
import { camelizeKeys } from 'humps'
import { isEmpty, keyBy, orderBy, uniqBy } from 'lodash'
import memoizee from 'memoizee'
import { defineStore } from 'pinia'
import { until } from '@vueuse/core'
import Message, {
  SendStatus,
  DEFAULT_AVATAR,
  TOURHERO_ADMIN_AVATAR,
  TOURHERO_AI_ASSISTANT_NAME,
  TOURHERO_AI_AVATAR,
} from '@/models/message'
import { FileMessage } from '@/models/fileMessage.ts'
import api from '@/utils/api.js'
import { deserializeJsonApi } from '@/utils/jsonApiDeserializer.js'
import { isFeatureEnabled } from '@/utils/featureUtils.js'
import { ChatroomStatus } from '@/stores/types'
import { generateTempId } from 'app/javascript/src/utils/chatUtils'
import { logErrorOnSentry } from '@/utils/errorReportingUtils'
import { fetchFullMessageContent } from '@/utils/chat/fullMessageContent'

export const useChannelsStore = defineStore('channels', {
  state: () => ({
    alias: null,
    currentChannelId: null,
    profiles: {},
    readStatus: {},
    channels: [],
    products: {},
    itineraryRequests: {},
    userMembers: {},
    operatorMembers: {},
    messages: {},
    isSearchOpen: false,
    searchString: '',
    searchSelection: null,
    searchResults: [],
    pusher: null,
    messageScroller: null,
    isMobileView: false,
    openMobileDetails: false,
    isModalOpen: false,
    tabs: [
      { name: 'Ongoing', active: true },
      { name: 'Archived', active: false },
    ],
    isChannelLoading: false,
  }),

  getters: {
    getCurrentChannel(state) {
      return state.channels.find(chatroom => chatroom.id == state.currentChannelId)
    },
    /**
     * @returns {import('./types/channels').Chatroom[]}
     */
    getOngoingChannels(state) {
      return state.channels.filter(
        channel => channel.status === ChatroomStatus.ACTIVE || channel.status === ChatroomStatus.INACTIVE,
      )
    },
    /**
     * @returns {import('./types/channels').Chatroom[]}
     */
    getArchivedChannels(state) {
      return state.channels.filter(channel => channel.status === ChatroomStatus.ARCHIVED)
    },
    getProfileName(state) {
      return isEmpty(state.profiles) ? '' : (state.profiles.own.name ?? state.alias)
    },
    getCurrentChannelName(state) {
      return state.channels.find(chatroom => chatroom.id == state.currentChannelId)?.name ?? 'TourHero Support'
    },
    getChannelPreviousMessage: state => (chatroomId, ownIndex) => {
      return state.messages[chatroomId][ownIndex - 1]
    },
    getChannelMessages: state => chatroomId => {
      return state.messages[chatroomId] ?? []
    },
    getCurrentChannelMessages(state) {
      return state.messages[state.currentChannelId?.toString()] ?? []
    },
    getChannelMessageIndex: state => id => {
      return state.messages[state.currentChannelId?.toString()].findIndex(message => message.id == id)
    },
    getChannelUnreadMessageIndex(state) {
      const unreadIndex = state.messages[state.currentChannelId?.toString()]?.findIndex(message => message.firstUnread)
      return unreadIndex == -1 ? null : unreadIndex
    },
    getChannelLastMessage: state => chatroomId => {
      return state.channels.find(chatroom => chatroom.id == chatroomId)?.lastMessage
    },
    getChannelCountry: state => chatroomId => {
      const chatroom = state.channels.find(chatroom => chatroom.id == chatroomId)
      return chatroom?.product?.itinerary?.targetCountryName ?? chatroom?.itineraryRequest?.country ?? null
    },
    getChannelMemberCount: state => chatroomId => {
      const userCount = Object.keys(state.userMembers[chatroomId.toString()] ?? {}).length
      const operatorCount = Object.keys(state.operatorMembers[chatroomId.toString()] ?? {}).length

      return userCount + operatorCount
    },
    getMembersProfile(state) {
      if (!state.currentChannelId) return {}

      const userProfiles = state.userMembers[state.currentChannelId] ?? []
      const operatorProfiles = state.operatorMembers[state.currentChannelId] ?? []
      return { ...userProfiles, ...operatorProfiles }
    },
    getItineraryRequest(state) {
      if (!state.currentChannelId) return null

      return state.itineraryRequests[state.currentChannelId] ?? null
    },
    getProduct(state) {
      if (!state.currentChannelId) return null

      return state.products[state.currentChannelId] ?? null
    },
    getChannel: state => id => {
      return state.channels.find(chatroom => chatroom.id == id) ?? null
    },
    getMessageAvatar: state => (chatroomId, senderId, senderName, supplierId) => {
      if (senderName == TOURHERO_AI_ASSISTANT_NAME) return TOURHERO_AI_AVATAR
      if (!senderId || senderName.includes('TourHero -')) return TOURHERO_ADMIN_AVATAR
      if (supplierId) return state.operatorMembers[chatroomId][supplierId]?.avatar ?? DEFAULT_AVATAR
      if (senderId) return state.userMembers[chatroomId][senderId]?.avatar ?? DEFAULT_AVATAR
      return DEFAULT_AVATAR
    },
    getChannelReadStatus: state => chatroomId => {
      if (!chatroomId) return true
      return state.readStatus[chatroomId]
    },
    getChannelLastReadMessageId: state => chatroomId => {
      if (!chatroomId) return undefined
      return state.channels.find(chatroom => chatroom.id == chatroomId)?.lastMessage?.id
    },
    hasReadAllMessages: state => chatroomId => {
      if (!chatroomId) return true
      return state.readStatus[chatroomId.toString()].all_read
    },
    isCurrentChannelActive(state) {
      return state.channels.find(chatroom => chatroom.id == state.currentChannelId)?.status === ChatroomStatus.ACTIVE
    },
    isAiActive(state) {
      return (
        isFeatureEnabled('custom_itinerary') &&
        state.channels.find(chatroom => chatroom.id == state.currentChannelId)?.aiActive
      )
    },
    getAiRedirectMessage() {
      return {
        id: 'ai-redirect-message',
        messageType: 'prompted',
        content:
          "Hi, I'm Maya — your travel expert!\n\nI'll help you fine-tune your itinerary, then pass it to our local guides who operate the trip to finalize.\n\nTo continue planning, let's go to your draft itinerary so that you can see any changes we make to your trip plan instantly.",
        createdAt: new Date().toISOString(),
        senderId: 'ai-assistant',
        senderName: 'Maya - AI Travel Assistant',
        displayName: true,
        options: [
          {
            text: 'Go to draft itinerary',
            action: 'redirect',
          },
        ],
      }
    },
    isLastMessageFetched(state) {
      const currentChatroomLastMessage = state.channels.find(
        chatroom => chatroom.id == state.currentChannelId,
      )?.lastMessage
      if (!currentChatroomLastMessage) return true

      return !!state.messages[state.currentChannelId]?.find(message => message.id == currentChatroomLastMessage.id)
    },
    currentChannelLastMessage(state) {
      return state.channels.find(chatroom => chatroom.id == state.currentChannelId)?.lastMessage
    },
    isArchived(state) {
      return state.channels.find(chatroom => chatroom.id == state.currentChannelId)?.status === ChatroomStatus.ARCHIVED
    },
  },
  actions: {
    nextTempId(channelId) {
      const lastLoadedMessage = this.messages[channelId]?.at(-1)
      return generateTempId(lastLoadedMessage?.id ?? this.getChannelLastMessage(channelId)?.id ?? 0)
    },
    setCurrentChannel(id) {
      this.currentChannelId = id
      this.isSearchOpen = false
      this.searchString = ''
    },
    closeSearch() {
      this.isSearchOpen = false
      this.searchString = ''
    },
    clearSearch() {
      this.searchString = ''
      this.searchResults = []
    },
    setChannelLastMessage(message, chatroomId) {
      const chatroom = this.channels.find(chatroom => chatroom.id == chatroomId)
      chatroom.lastMessage = message
    },
    async restoreTruncatedContent(chatroomId, messageId) {
      const content = await fetchFullMessageContent(chatroomId, messageId)
      if (content === null) return

      const message = this.messages[chatroomId]?.find(message => message.id == messageId)
      if (message) message.content = content

      const lastMessage = this.getChannelLastMessage(chatroomId)
      if (lastMessage?.id == messageId) {
        this.setChannelLastMessage({ ...lastMessage, content }, chatroomId)
      }
    },
    setChatroomMessages(messages, chatroomId) {
      this.messages[chatroomId] = uniqBy(messages, 'id')
    },
    setIsViewingPastMessages(chatroomId, boolean) {
      this.readStatus[chatroomId.toString()] = {
        ...this.readStatus[chatroomId],
        isViewingPastMessages: boolean,
      }
    },
    setChannelAllRead(chatroomId, boolean) {
      this.readStatus[chatroomId.toString()] = { ...this.readStatus[chatroomId], all_read: boolean }
    },
    setSearchSelection(searchResultIndex) {
      this.searchSelection = searchResultIndex
    },
    setChannelData(channels, autoLoadFirstChannel = true) {
      this.channels = orderBy(
        channels,
        [chatroom => chatroom.lastMessage?.createdAt || '', 'createdAt'],
        ['desc', 'desc'],
      )

      this.channels.forEach(channel => {
        if (channel.userMembers)
          this.userMembers[channel.id] = {
            ...this.userMembers[channel.id],
            ...keyBy(channel.userMembers, 'id'),
          }
        if (channel.operatorMembers)
          this.operatorMembers[channel.id] = {
            ...this.operatorMembers[channel.id],
            ...keyBy(channel.operatorMembers, 'id'),
          }
        if (channel.product)
          this.products[channel.id] = {
            ...channel.product,
            event: channel.product.displayableEvent,
          }
        if (channel.itineraryRequest) this.itineraryRequests[channel.id] = channel.itineraryRequest
      })

      if (this.channels.length && autoLoadFirstChannel && !this.isMobileView) {
        this.currentChannelId = this.getOngoingChannels[0]?.id ?? null
      }
    },
    setMessageScroller(scroller) {
      this.messageScroller = scroller
    },
    async fetchChannelMessages(chatroom_id) {
      const messages = await apiGetChatroomMessages(chatroom_id)
      this.setChatroomMessages(uniqBy(messages, 'id'), chatroom_id)
    },
    async getChannelUnreadMessages() {
      const results = await api.get(`/api/chatrooms/${this.currentChannelId}/messages/unread_messages`)
      const messages = await deserializeJsonApi(results.data)
      this.setChatroomMessages(uniqBy(messages, 'id'), this.currentChannelId)
    },
    toggleOpenMobileDetails() {
      this.openMobileDetails = !this.openMobileDetails
    },
    async addNewChannel(payload) {
      const channel = await deserializeJsonApi(payload)

      // re-added into only chatroom
      if (this.channels?.some(chatroom => chatroom.id == channel.id) && this.channels.length == 1) {
        window.location.reload()
        return
      }

      this.channels = this.channels.filter(existingChannel => existingChannel.id != channel.id)

      this.channels = [channel, ...this.channels]
      this.messages[channel.id] = []
      this.readStatus[channel.id] = payload.meta.read_status

      if (channel.userMembers) this.userMembers[channel.id] = keyBy(channel.userMembers, 'id')
      if (channel.operatorMembers) this.operatorMembers[channel.id] = keyBy(channel.operatorMembers, 'id')
      if (channel.product) this.products[channel.id] = { ...channel.product, event: channel.product?.displayableEvent }
      if (channel.itineraryRequest) this.itineraryRequests[channel.id] = channel.itineraryRequest
    },
    async removeChannel(payload) {
      const removedChannel = await deserializeJsonApi(payload)
      const chatroom = this.channels.find(chatroom => chatroom.id == removedChannel.id)

      if (!chatroom) return

      chatroom.status = ChatroomStatus.INACTIVE
    },
    addNewMessage(payload) {
      const camelizedPayload = camelizeKeys(payload)
      const chatroomMessages = this.messages[camelizedPayload.meta.chatroomId]
      const own_message = chatroomMessages?.find(message => message.id == camelizedPayload.meta.tempId)

      const lastMessage = this.getChannelLastMessage(camelizedPayload.meta.chatroomId)

      if (own_message) {
        own_message.sendSucceeded()
      } else {
        if (chatroomMessages?.some(message => message.id == camelizedPayload.message.id)) return

        const lastMessageId = lastMessage ? parseInt(lastMessage.id.toString().split('-')[0], 10) : NaN
        const incomingId = parseInt(camelizedPayload.message.id, 10)
        const isStale = Number.isFinite(lastMessageId) && Number.isFinite(incomingId) && incomingId <= lastMessageId

        const newMessagePayload = {
          ...camelizedPayload.message,
          displayName: lastMessage?.senderId !== camelizedPayload.message.senderId,
        }

        if (chatroomMessages) {
          this.messages[camelizedPayload.meta.chatroomId] =
            this.messages[camelizedPayload.meta.chatroomId].concat(newMessagePayload)
        }

        if (!isStale) {
          this.setChannelLastMessage(newMessagePayload, camelizedPayload.meta.chatroomId)
        }

        if (newMessagePayload.contentTruncated && (chatroomMessages || !isStale)) {
          this.restoreTruncatedContent(camelizedPayload.meta.chatroomId, newMessagePayload.id)
        }

        if (!isStale) {
          this.setChannelAllRead(camelizedPayload.meta.chatroomId, false)

          const isLastMessageFetched = this.isLastMessageFetched
          const isViewingPastMessages = this.readStatus[this.currentChannelId]?.isViewingPastMessages ?? false

          if (
            isLastMessageFetched &&
            !isViewingPastMessages &&
            camelizedPayload.meta.chatroomId == this.currentChannelId
          ) {
            setTimeout(() => {
              this.messageScroller?.scrollToBottom()
              this.setChannelAllRead(camelizedPayload.meta.chatroomId, true)
            }, 400)
          }
        }
      }
    },
    updateChatroomName(payload) {
      const camelizedPayload = camelizeKeys(payload)
      const chatroom = this.channels.find(chatroom => chatroom.id == camelizedPayload.meta.chatroomId)

      chatroom.name = camelizedPayload.chatName
    },
    async updateChatroomMembers(payload) {
      const data = await deserializeJsonApi(payload)
      this.userMembers[payload.meta.chatroom_id] = keyBy(data.userMembers, 'id')
      this.operatorMembers[payload.meta.chatroom_id] = keyBy(data.operatorMembers, 'id')
    },
    async updateChatroomAssociations(payload) {
      const chatroom = this.channels.find(chatroom => chatroom.id == payload.meta.chatroom_id)

      if (!payload.data) {
        if (payload.meta.association_name === 'Product') delete this.products[chatroom.id]
        if (payload.meta.association_name === 'ItineraryRequest') delete this.itineraryRequests[chatroom.id]
      } else {
        const data = await deserializeJsonApi(payload)

        if (payload.meta.association_name === 'Product')
          this.products[chatroom.id] = { ...data, event: data?.displayableEvent }
        if (payload.meta.association_name === 'ItineraryRequest') this.itineraryRequests[chatroom.id] = data
      }
    },
    async fetchUserChannels() {
      this.isChannelLoading = true
      const result = await api.get('/api/chatrooms')
      const data = await deserializeJsonApi(result.data)

      this.alias = result.data.meta.alias
      this.profiles = result.data.meta.profiles
      this.readStatus = result.data.meta.read_status
      this.setChannelData(data)
      this.isChannelLoading = false

      return { meta: result.data.meta, data: data }
    },
    async sendMessage(text) {
      if (!text) return

      const isViewingPastMessages = this.readStatus[this.currentChannelId]?.isViewingPastMessages ?? false

      if (isViewingPastMessages) {
        await this.fetchChannelMessages(this.currentChannelId)
        this.setIsViewingPastMessages(this.currentChannelId, false)
      }

      const tempId = this.nextTempId(this.currentChannelId)
      const textMessage = new Message({
        tempId,
        content: text,
        channelId: this.currentChannelId,
        senderName: this.alias,
        senderId: this.profiles.own.slug,
        sendStatus: SendStatus.SENDING,
        displayName: this.currentChannelLastMessage
          ? this.currentChannelLastMessage.senderId !== this.profiles.own.slug
          : true,
        updateMessageCallback: this.updateMessage,
      })

      this.messages[this.currentChannelId] = this.messages[this.currentChannelId].concat(textMessage)
      this.setChannelLastMessage(textMessage, this.currentChannelId)
      setTimeout(() => this.messageScroller?.scrollToBottom(), 400)

      // we do not need to wait for promise to resolve as we will be getting response via pusher
      api.post(`/api/chatrooms/${this.currentChannelId}/messages`, {
        temp_id: tempId,
        content: text,
        sender_name: this.alias,
      })
    },
    async sendMessasgeOnChannelReady(text) {
      until(() => this.messages[this.currentChannelId])
        .toBeTruthy()
        .then(() => this.sendMessage(text))
    },
    async sendAttachment(files, abortController) {
      const isViewingPastMessages = this.readStatus[this.currentChannelId]?.isViewingPastMessages ?? false

      if (isViewingPastMessages) {
        await this.fetchChannelMessages(this.currentChannelId)
        this.setIsViewingPastMessages(this.currentChannelId, false)
      }

      const tempId = this.nextTempId(this.currentChannelId)
      const fileMessage = new FileMessage(
        {
          tempId,
          content: '',
          channelId: this.currentChannelId,
          senderName: this.alias,
          senderId: this.profiles.own.slug,
          sendStatus: SendStatus.UPLOADING,
          updateMessageCallback: this.updateMessage,
        },
        files,
        abortController,
      )

      this.messages[this.currentChannelId] = this.messages[this.currentChannelId].concat(fileMessage)
      setTimeout(() => this.messageScroller?.scrollToBottom(), 400)

      // The FileMessage constructor already rejects oversized and unsupported
      // files, so stop before the upload. Without this the whole body still
      // goes up the wire only to be refused by the server, which on a slow
      // connection is minutes of progress bar for a verdict the browser
      // reached instantly, and the response overwrites the local error text.
      if (fileMessage.sendStatus === SendStatus.FAIL) return

      try {
        const result = await api.post(
          `/api/chatrooms/${this.currentChannelId}/messages/attach`,
          { temp_id: tempId, content: '', sender_name: this.alias, files: files },
          {
            signal: abortController.signal,
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: function (progressEvent) {
              fileMessage.updateProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
            },
          },
        )
        const data = await deserializeJsonApi(result.data)
        fileMessage.sendSucceeded(data.attachments, data.messageType)
      } catch (e) {
        if (e instanceof AxiosError && e.response?.data?.error) {
          fileMessage.sendFailed(e.response.data.error)
        } else {
          fileMessage.sendFailed('Something went wrong, please try again.')
        }
      }
    },
    cancelAttachment(messageId) {
      const fileMessage = this.messages[this.currentChannelId].find(message => message.id == messageId)
      fileMessage.cancel()
    },
    updateMessage(channelId, messageId, property, newValue) {
      const message = this.messages[channelId.toString()]?.find(message => message.id == messageId)
      message[property] = newValue
    },
    async read(chatroomId) {
      if (!chatroomId) return
      try {
        const response = await api.put(`/api/chatrooms/${chatroomId}/messages/read`)
        this.readStatus[chatroomId] = { ...this.readStatus[chatroomId], ...response.data.read_status }
      } catch (error) {
        logErrorOnSentry(error, {}, { chatroomId })
      }
    },
    async fetchPrevious(messageId, trigger) {
      // mark trigger as fetched to prevent re-fetching
      this.updateMessage(this.currentChannelId, messageId, 'trigger', { ...trigger, fetched: true })

      const results = await api.get(`/api/chatrooms/${this.currentChannelId}/messages/fetch_previous`, {
        params: { cursor: trigger.batchCursor },
      })
      const messages = await deserializeJsonApi(results.data)

      this.messages[this.currentChannelId] = orderBy(
        uniqBy([...messages, ...this.messages[this.currentChannelId]], 'id'),
        [message => parseInt(message.id)],
        ['asc'],
      )
    },
    async fetchNext(messageId, trigger) {
      // mark trigger as fetched to prevent re-fetching
      this.updateMessage(this.currentChannelId, messageId, 'trigger', { ...trigger, fetched: true })

      const results = await api.get(`/api/chatrooms/${this.currentChannelId}/messages/fetch_next`, {
        params: { cursor: trigger.batchCursor },
      })
      const messages = await deserializeJsonApi(results.data)

      this.messages[this.currentChannelId] = orderBy(
        uniqBy([...this.messages[this.currentChannelId], ...messages], 'id'),
        [message => parseInt(message.id)],
        ['asc'],
      )
    },
    async search(searchString) {
      const results = await api.get(`/api/chatrooms/${this.currentChannelId}/messages/search`, {
        params: { search: searchString },
      })
      const messages = await deserializeJsonApi(results.data)

      this.isSearchOpen = true
      this.searchResults = [...messages]
      this.searchSelection = null
    },
    async searchResultMessages(resultId) {
      const result = await api.get(`/api/chatrooms/${this.currentChannelId}/messages/${resultId}/search_result`)
      const searchResult = await deserializeJsonApi(result.data)
      this.messages[this.currentChannelId] = [
        ...searchResult.beforeMessages,
        searchResult,
        ...searchResult.afterMessages,
      ]

      // we add an extra offset because the message sometimes ends up outside (above) of
      // scroller, due to variable height image / product cards
      const scrollIndexWithOffset = Math.max(searchResult.beforeMessages?.length - 2, 0)

      if (this.isMobileView) {
        this.openMobileDetails = false

        setTimeout(() => {
          const scrollIndex = this.messages[this.currentChannelId]?.findIndex(message => message.id === searchResult.id)
          this.messageScroller?.scrollToIndex(scrollIndex ?? scrollIndexWithOffset)
        }, 700)
      } else {
        setTimeout(() => this.messageScroller?.scrollToIndex(scrollIndexWithOffset), 700)
      }
    },
    async searchWithResultIndex(index) {
      if (index < 0 || index >= this.searchResults.length) return

      const searchMessageId = this.searchResults[index].id

      const result = await api.get(`/api/chatrooms/${this.currentChannelId}/messages/${searchMessageId}/search_result`)
      const searchResult = await deserializeJsonApi(result.data)

      // we add an extra offset because the message sometimes ends up outside (above) of
      // scroller, due to variable height image / product cards
      const scrollIndexWithOffset = Math.max(searchResult.beforeMessages?.length - 2, 0)

      this.messages[this.currentChannelId] = [
        ...searchResult.beforeMessages,
        searchResult,
        ...searchResult.afterMessages,
      ]

      if (this.messageScroller) {
        setTimeout(() => this.messageScroller.scrollToIndex(scrollIndexWithOffset), 700)
      }
    },
    async sendInvites(user_emails) {
      const result = await api.post(`/api/chatrooms/${this.currentChannelId}/invite_members`, {
        user_emails: user_emails,
      })
      const data = await deserializeJsonApi(result.data)

      if (data.userMembers) this.userMembers[this.currentChannelId] = data.userMembers
    },
    async getProductCardDetails(product_id) {
      const result = await api.get(`/api/chatrooms/${this.currentChannelId}/messages/product_card_details`, {
        params: { product_id: product_id },
      })
      return await deserializeJsonApi(result.data)
    },
    async initializeChatModal(itineraryRequestId, autoload = false) {
      const result = await apiCreateItineraryRequestChatroom(itineraryRequestId)
      const data = await deserializeJsonApi(result.data)
      this.alias = result.data.meta.alias
      this.profiles = result.data.meta.profiles
      this.readStatus = result.data.meta.read_status
      this.setChannelData(data, autoload)
    },
    async initializePlanATripChat(itineraryRequestId) {
      await this.initializeChatModal(itineraryRequestId, true)
    },
    closeChatModal() {
      this.isModalOpen = false
    },
    openChatModal() {
      this.isModalOpen = true
    },
    setMobileView(value) {
      this.isMobileView = value
    },
    /**
     * @param {string} tabName
     */
    setActiveTab(tabName) {
      this.tabs = this.tabs.map(tab => ({
        ...tab,
        active: tab.name === tabName,
      }))
    },
    async archiveChatroom() {
      const response = await api.post(`/api/chatrooms/${this.currentChannelId}/archive`)
      const data = await deserializeJsonApi(response.data)
      const archivedChannel = this.channels.find(chatroom => chatroom.id == data.id)
      archivedChannel.status = data.status

      this.resetSelectedChannel()
    },
    async handlePusherChatroomArchived(payload) {
      const data = await deserializeJsonApi(payload)

      this.channels = this.channels.map(chatroom => {
        if (chatroom.id !== data.id) {
          return chatroom
        }

        return { ...chatroom, status: ChatroomStatus.ARCHIVED }
      })

      if (this.currentChannelId !== data.id) {
        return
      }

      this.resetSelectedChannel()
    },
    async resetSelectedChannel() {
      if (!this.isMobileView) {
        this.currentChannelId = this.getOngoingChannels[0]?.id ?? null
        return
      }

      this.setCurrentChannel(null)
      this.closeChatModal()
      this.toggleOpenMobileDetails()
    },
  },
})

export const apiCreateItineraryRequestChatroom = memoizee(itineraryRequestId => {
  return api.post('/api/chatrooms', {
    chatroom: {
      itinerary_request_id: itineraryRequestId,
    },
  })
})

export const apiGetChatroomMessages = memoizee(
  async (chatroomId, params = {}) => {
    const results = await api.get(`/api/chatrooms/${chatroomId}/messages/latest_messages`, { params })
    return await deserializeJsonApi(results.data)
  },
  { length: 2 },
)
